/**
 * KAN-811: одно правило "куда менеджеру можно" (allow-list путей кабинета).
 * Им пользуются ManagerRouteGuard и меню (Sidebar: пункт виден менеджеру, если его путь здесь).
 */

/** Роль менеджера (вход в кабинет у записи справочника менеджеров). */
export const MANAGER_ROLE = 'MANAGER';

/**
 * Разрешённые менеджеру пути: точное совпадение после нормализации.
 * Карточка клиента и его переписка, карточка организации (KAN-826)
 * проверяются отдельными шаблонами.
 */
const MANAGER_EXACT_PATHS = new Set(['/', '/crm', '/leads', '/clients', '/companies', '/emails', '/profile']);

/** /clients/:id и /clients/:id/chat, кроме раздела обзвона (/clients/cold-calls*). */
const CLIENT_PATH = /^\/clients\/([^/]+)(\/chat)?$/;
const COLD_CALLS_SEGMENT = 'cold-calls';
/** KAN-826: карточка организации клиента /companies/:id. */
const COMPANY_PATH = /^\/companies\/[^/]+$/;
/** KAN-951: раздел "Помощь" /help и /help/:slug. */
const HELP_PATH = /^\/help(\/[^/]+)?$/;
/**
 * KAN-834: приложение менеджера: /m, /m/leads, /m/clients/:id; KAN-837: /m/profile; KAN-845: /m/start;
 * KAN-851: /m/companies/:id; KAN-862: /m/board; KAN-964: /m/meetings.
 */
const MOBILE_APP_PATH = /^\/m(\/leads|\/board|\/meetings|\/profile|\/start|\/clients\/[^/]+|\/companies\/[^/]+)?$/;

function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '') || '/';
  return pathname;
}

export function isPathAllowedForManager(pathname: string): boolean {
  const path = normalize(pathname);
  if (MANAGER_EXACT_PATHS.has(path) || COMPANY_PATH.test(path) || HELP_PATH.test(path) || MOBILE_APP_PATH.test(path)) return true;
  const match = CLIENT_PATH.exec(path);
  return match !== null && match[1] !== COLD_CALLS_SEGMENT;
}

export function isManagerUser(user: { role?: string } | null | undefined): boolean {
  return user?.role === MANAGER_ROLE;
}

/**
 * Запросы к API, открытые менеджеру. Список правил присылает backend в
 * /auth/me и ответе входа (поле api_access, тот же список, что у шлюза
 * backend/api/access/manager_access.py); своей копии у фронта нет. Блоки и
 * кнопки с закрытым запросом менеджеру не показываются, их запросы не уходят.
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Правило доступа от backend: методы и регулярка пути от /api/v1/ (совместима с JS). */
export interface ApiAccessRule {
  methods: string[];
  pattern: string;
}

/** Любой uuid для проверки доступа, когда id под рукой нет (правила backend ждут uuid). */
export const ANY_UUID = '00000000-0000-0000-0000-000000000000';

const API_PREFIX = /^\/?(api\/v1\/)?/;

interface CompiledRule {
  methods: ReadonlySet<string>;
  regex: RegExp | null;
}

const compiledCache = new WeakMap<readonly ApiAccessRule[], CompiledRule[]>();

function compile(rules: readonly ApiAccessRule[]): CompiledRule[] {
  const cached = compiledCache.get(rules);
  if (cached) return cached;
  const compiled = rules.map((rule) => {
    let regex: RegExp | null = null;
    try {
      regex = new RegExp(rule.pattern);
    } catch {
      // битое правило ничего не открывает
    }
    return { methods: new Set(rule.methods), regex };
  });
  compiledCache.set(rules, compiled);
  return compiled;
}

/**
 * Открыт ли менеджеру запрос по правилам backend: путь API ("/api/v1/..."),
 * query отбрасывается. Без правил (поле не пришло) закрыто всё.
 */
export function isApiAllowedForManager(
  method: HttpMethod,
  url: string,
  apiAccess: readonly ApiAccessRule[] | null | undefined
): boolean {
  if (!apiAccess) return false;
  const tail = url.split(/[?#]/)[0].replace(API_PREFIX, '');
  return compile(apiAccess).some((rule) => rule.methods.has(method) && !!rule.regex?.test(tail));
}

/** Может ли пользователь сделать запрос: всем, кроме менеджера, без ограничений. */
export function canUserCallApi(
  user: { role?: string; api_access?: readonly ApiAccessRule[] | null } | null | undefined,
  method: HttpMethod,
  url: string
): boolean {
  return !isManagerUser(user) || isApiAllowedForManager(method, url, user?.api_access);
}

/** KAN-828: роли, которые ставят признак "Старший менеджер": владелец и оператор. */
const SENIOR_MANAGER_SETTER_ROLES: ReadonlySet<string> = new Set(['OWNER', 'USER']);

/**
 * KAN-828: может ли пользователь ставить признак "Старший менеджер".
 * Явный список ролей (SENIOR_MANAGER_SETTER_ROLES), новая роль доступа не получает.
 */
export function canSetSeniorManager(user: { role?: string } | null | undefined): boolean {
  return !!user?.role && SENIOR_MANAGER_SETTER_ROLES.has(user.role);
}
